import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores/useAppStore';
import { clearTokenCookie } from '../lib/authToken';
import './TopBar.css';

const IN_UNIVERSE_PATHS = ['/slugs', '/workspace', '/chat', '/universe-settings'];
const GLOBAL_PATHS = ['/library', '/player', '/settings', '/archive', '/log'];

function isInUniverse(pathname: string): boolean {
  return IN_UNIVERSE_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

function isGlobal(pathname: string): boolean {
  return GLOBAL_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

export default function TopBar() {
  const location = useLocation();
  const nav = useNavigate();
  const activeUniverseId = useAppStore((s) => s.ui.activeUniverseId);
  const universes = useAppStore((s) => s.universes);
  const universe = activeUniverseId ? universes[activeUniverseId] : undefined;
  const setSettings = useAppStore((s) => s.setSettings);
  const hasToken = useAppStore((s) => !!s.settings.apiToken);
  const [menuOpen, setMenuOpen] = useState(false);

  function onLogout() {
    if (!confirm('Sign out and clear the saved token on this device?')) return;
    clearTokenCookie();
    setSettings({ apiToken: undefined });
    setMenuOpen(false);
    nav('/');
  }

  const inUniverse = isInUniverse(location.pathname);
  const global = isGlobal(location.pathname);
  const isHome = location.pathname === '/';

  function go(to: string) {
    setMenuOpen(false);
    nav(to);
  }

  function onBack() {
    if (/^\/chat\/.+/.test(location.pathname)) {
      nav('/chat');
      return;
    }
    if (global && window.history.length > 1) {
      nav(-1);
    } else {
      nav('/');
    }
  }

  const title = inUniverse && universe ? universe.name : 'Bedtimer';

  return (
    <header className="top-bar">
      {!isHome ? (
        <button
          className="top-bar-back"
          onClick={onBack}
          aria-label={global ? 'Back' : 'Back to universes'}
        >
          ←
        </button>
      ) : (
        <span className="top-bar-back-spacer" aria-hidden />
      )}
      <button
        className="top-bar-title"
        onClick={() => inUniverse && go('/universe-settings')}
        disabled={!inUniverse}
      >
        {title}
      </button>
      <button
        className="top-bar-menu"
        onClick={() => setMenuOpen(true)}
        aria-label="Menu"
      >
        ☰
      </button>
      {menuOpen && (
        <>
          <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />
          <div className="menu-panel">
            <button className="menu-item" onClick={() => go('/')}>
              Universes
            </button>
            <button className="menu-item" onClick={() => go('/library')}>
              Library
            </button>
            {inUniverse && (
              <button className="menu-item" onClick={() => go('/universe-settings')}>
                Universe settings
              </button>
            )}
            <button className="menu-item" onClick={() => go('/settings')}>
              Settings
            </button>
            <button className="menu-item" onClick={() => go('/archive')}>
              Archive
            </button>
            {hasToken && (
              <button className="menu-item" onClick={onLogout}>
                Sign out
              </button>
            )}
          </div>
        </>
      )}
    </header>
  );
}
